import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Mail, Phone, MapPin, Calendar as CalIcon, PoundSterling, Star, Check, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, estimates: 0, reviews_pending: 0, reviews_approved: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("pending");
  const [tab, setTab] = useState("quotes");

  const load = async () => {
    setLoading(true);
    try {
      const [q, e, r, s] = await Promise.all([
        api.get("/quotes"),
        api.get("/admin/estimates"),
        api.get("/admin/reviews"),
        api.get("/admin/stats"),
      ]);
      setQuotes(q.data);
      setEstimates(e.data);
      setReviews(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const moderateReview = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success(`Review ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success("Review deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success(`Marked as ${status}`);
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const filteredQuotes = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-white text-[var(--jb-navy)]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-[var(--jb-line)] bg-white sticky top-0 z-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <picture>
              <source srcSet="/logo-jll.webp" type="image/webp" />
              <img
                src="/logo-jll.png"
                alt="JLL"
                className="h-9 w-auto object-contain"
                width="228"
                height="240"
                decoding="async"
              />
            </picture>
            <div>
              <div className="serif text-[15px] leading-tight">Jaybliss Admin</div>
              <div className="mono-cap text-[9px] text-[var(--jb-muted)]">Quote Console</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} className="mono-cap inline-flex items-center gap-2 text-[var(--jb-muted)] hover:text-[var(--jb-navy)]" data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <Link to="/" className="mono-cap text-[var(--jb-muted)] hover:text-[var(--jb-navy)]">View site</Link>
            <button
              onClick={logout}
              className="mono-cap inline-flex items-center gap-2 text-[var(--jb-rust)]"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="mono-cap text-[var(--jb-rust)]">Overview</div>
            <h1 className="serif text-4xl md:text-5xl leading-tight mt-2">
              Welcome back, {user?.name?.split(" ")[0] || "Admin"}.
            </h1>
          </div>
          <div className="mono-cap text-[var(--jb-muted)]">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
          {[
            { l: "Total quotes", v: stats.total },
            { l: "Pending", v: stats.pending, accent: true },
            { l: "Confirmed", v: stats.confirmed },
            { l: "Completed", v: stats.completed },
            { l: "Estimator leads", v: stats.estimates || 0, rust: true },
            { l: "Reviews pending", v: stats.reviews_pending || 0, accent: true },
          ].map((s) => (
            <div key={s.l} className="border border-[var(--jb-line)] p-6" data-testid={`stat-${s.l.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="mono-cap text-[var(--jb-muted)]">{s.l}</div>
              <div
                className={`serif text-5xl mt-2 ${
                  s.accent ? "text-[var(--jb-rust)]" : s.rust ? "text-[var(--jb-rust)]" : "text-[var(--jb-navy)]"
                }`}
              >
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent p-0 border-b border-[var(--jb-line)] rounded-none w-full justify-start gap-6 h-auto">
            <TabsTrigger
              value="quotes"
              className="mono-cap rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--jb-rust)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--jb-navy)] data-[state=active]:shadow-none px-1 py-3 text-[var(--jb-muted)]"
              data-testid="tab-quotes"
            >
              Quote Requests ({quotes.length})
            </TabsTrigger>
            <TabsTrigger
              value="estimates"
              className="mono-cap rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--jb-rust)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--jb-navy)] data-[state=active]:shadow-none px-1 py-3 text-[var(--jb-muted)]"
              data-testid="tab-estimates"
            >
              Estimator Leads ({estimates.length})
            </TabsTrigger>
            <TabsTrigger
              value="reviews"
              className="mono-cap rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--jb-rust)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--jb-navy)] data-[state=active]:shadow-none px-1 py-3 text-[var(--jb-muted)]"
              data-testid="tab-reviews"
            >
              Reviews ({reviews.length}) {stats.reviews_pending > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--jb-rust)] text-[var(--jb-bone)] text-[10px] font-bold">{stats.reviews_pending}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ---- Quotes ---- */}
          <TabsContent value="quotes" className="mt-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {["all", ...STATUSES].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`mono-cap px-3 py-1.5 border ${
                    filter === s
                      ? "bg-[var(--jb-navy)] text-[var(--jb-bone)] border-[var(--jb-navy)]"
                      : "border-[var(--jb-line)] text-[var(--jb-muted)] hover:text-[var(--jb-navy)]"
                  }`}
                  data-testid={`filter-${s}`}
                >
                  {s}
                </button>
              ))}
              <div className="ml-auto mono-cap text-[var(--jb-muted)]">{filteredQuotes.length} quotes</div>
            </div>

            <div className="border border-[var(--jb-line)] overflow-hidden">
              <div className="hidden md:grid grid-cols-12 mono-cap text-[var(--jb-muted)] bg-[var(--jb-bone)] px-6 py-3 border-b border-[var(--jb-line)]">
                <div className="col-span-2">Customer</div>
                <div className="col-span-2">Contact</div>
                <div className="col-span-2">Route</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Service</div>
                <div className="col-span-2">Status</div>
              </div>
              {loading ? (
                <div className="p-10 mono-cap text-[var(--jb-muted)]">Loading…</div>
              ) : filteredQuotes.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="serif text-2xl text-[var(--jb-muted)]">No quotes yet.</div>
                </div>
              ) : (
                filteredQuotes.map((q, i) => (
                  <motion.button
                    key={q.id}
                    onClick={() => setSelected({ kind: "quote", ...q })}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="w-full text-left grid grid-cols-2 md:grid-cols-12 gap-y-2 gap-x-4 items-center px-6 py-5 border-b border-[var(--jb-line)] hover:bg-[var(--jb-bone)] transition-colors"
                    data-testid={`quote-row-${q.id}`}
                  >
                    <div className="md:col-span-2">
                      <div className="serif text-lg">{q.name}</div>
                      <div className="mono-cap text-[10px] text-[var(--jb-muted)] mt-1">
                        {new Date(q.created_at).toLocaleDateString("en-GB")}
                      </div>
                    </div>
                    <div className="md:col-span-2 text-sm">
                      <div>{q.phone}</div>
                      <div className="text-[var(--jb-muted)] text-xs">{q.email}</div>
                    </div>
                    <div className="md:col-span-2 text-sm">
                      <span>{q.from_postcode}</span>
                      <span className="mx-2 text-[var(--jb-rust)]">→</span>
                      <span>{q.to_postcode}</span>
                    </div>
                    <div className="md:col-span-2 text-sm">{q.move_date}</div>
                    <div className="md:col-span-2 text-sm">{q.service}</div>
                    <div className="md:col-span-2">
                      <span className={`status-pill status-${q.status}`}>{q.status}</span>
                    </div>
                  </motion.button>
                ))
              )}
            </div>
          </TabsContent>

          {/* ---- Estimates ---- */}
          <TabsContent value="estimates" className="mt-6">
            <div className="mono-cap text-[var(--jb-muted)] mb-4">
              Leads captured from the on-site Instant Ballpark Estimator.
            </div>
            <div className="border border-[var(--jb-line)] overflow-hidden">
              <div className="hidden md:grid grid-cols-12 mono-cap text-[var(--jb-muted)] bg-[var(--jb-bone)] px-6 py-3 border-b border-[var(--jb-line)]">
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Crew</div>
                <div className="col-span-2">Hours × Miles</div>
                <div className="col-span-2">Move date</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-1">Helpers</div>
              </div>
              {loading ? (
                <div className="p-10 mono-cap text-[var(--jb-muted)]">Loading…</div>
              ) : estimates.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="serif text-2xl text-[var(--jb-muted)]">No estimator leads yet.</div>
                  <p className="mono-cap text-[var(--jb-muted)] mt-2">
                    Leads captured from the hero estimator will appear here.
                  </p>
                </div>
              ) : (
                estimates.map((e, i) => (
                  <motion.button
                    key={e.id}
                    onClick={() => setSelected({ kind: "estimate", ...e })}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="w-full text-left grid grid-cols-2 md:grid-cols-12 gap-y-2 gap-x-4 items-center px-6 py-5 border-b border-[var(--jb-line)] hover:bg-[var(--jb-bone)] transition-colors"
                    data-testid={`estimate-row-${e.id}`}
                  >
                    <div className="md:col-span-3">
                      <div className="serif text-lg break-all">{e.email}</div>
                      <div className="mono-cap text-[10px] text-[var(--jb-muted)] mt-1">
                        {new Date(e.created_at).toLocaleString("en-GB")}
                      </div>
                    </div>
                    <div className="md:col-span-2 text-sm">
                      {e.crew === "2men" ? "2 Men + Van" : e.crew === "1man" ? "1 Man + Van" : (e.size || "—")}
                    </div>
                    <div className="md:col-span-2 text-sm">
                      {(e.hours ? `${e.hours}h` : "—")}
                      <span className="mx-2 text-[var(--jb-rust)]">·</span>
                      {e.distance_miles} mi
                    </div>
                    <div className="md:col-span-2 text-sm">{e.move_date || "—"}</div>
                    <div className="md:col-span-2 serif text-2xl text-[var(--jb-rust)]">£{e.price}</div>
                    <div className="md:col-span-1 mono-cap text-[11px]">
                      {e.helpers ?? 0}
                    </div>
                  </motion.button>
                ))
              )}
            </div>
          </TabsContent>

          {/* ---- Reviews ---- */}
          <TabsContent value="reviews" className="mt-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {["all", "pending", "approved", "rejected"].map((s) => (
                <button
                  key={s}
                  onClick={() => setReviewFilter(s)}
                  className={`mono-cap px-3 py-1.5 border ${
                    reviewFilter === s
                      ? "bg-[var(--jb-navy)] text-[var(--jb-bone)] border-[var(--jb-navy)]"
                      : "border-[var(--jb-line)] text-[var(--jb-muted)] hover:text-[var(--jb-navy)]"
                  }`}
                  data-testid={`review-filter-${s}`}
                >
                  {s}
                </button>
              ))}
              <div className="ml-auto mono-cap text-[var(--jb-muted)]">
                {reviews.filter((r) => reviewFilter === "all" || r.status === reviewFilter).length} reviews
              </div>
            </div>

            <div className="grid gap-4">
              {loading ? (
                <div className="p-10 mono-cap text-[var(--jb-muted)] bg-white border border-[var(--jb-line)]">Loading…</div>
              ) : reviews.filter((r) => reviewFilter === "all" || r.status === reviewFilter).length === 0 ? (
                <div className="p-10 text-center bg-white border border-[var(--jb-line)]">
                  <div className="serif text-2xl text-[var(--jb-muted)]">No {reviewFilter === "all" ? "" : reviewFilter} reviews.</div>
                </div>
              ) : (
                reviews
                  .filter((r) => reviewFilter === "all" || r.status === reviewFilter)
                  .map((r, i) => (
                    <motion.article
                      key={r.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: i * 0.03 }}
                      className="bg-white border border-[var(--jb-line)] p-6 md:p-7"
                      data-testid={`admin-review-${r.id}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="serif text-xl text-[var(--jb-navy)]">{r.name}</span>
                            <span className={`status-pill status-${r.status === "approved" ? "completed" : r.status === "rejected" ? "cancelled" : "pending"}`}>{r.status}</span>
                          </div>
                          <div className="flex items-center gap-1 mt-2">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star
                                key={n}
                                className={`w-4 h-4 ${n <= r.rating ? "text-[var(--jb-rust)] fill-[var(--jb-rust)]" : "text-[var(--jb-navy)]/20"}`}
                                strokeWidth={1.5}
                              />
                            ))}
                            <span className="mono-cap text-[10px] text-[var(--jb-muted)] ml-2">
                              {new Date(r.created_at).toLocaleString("en-GB")}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {r.status !== "approved" && (
                            <button
                              onClick={() => moderateReview(r.id, "approved")}
                              className="mono-cap inline-flex items-center gap-1.5 bg-[var(--jb-navy)] text-[var(--jb-bone)] px-3 py-2 hover:bg-[var(--jb-rust)] transition-colors"
                              data-testid={`approve-${r.id}`}
                            >
                              <Check className="w-3.5 h-3.5" /> Approve
                            </button>
                          )}
                          {r.status !== "rejected" && (
                            <button
                              onClick={() => moderateReview(r.id, "rejected")}
                              className="mono-cap inline-flex items-center gap-1.5 border border-[var(--jb-line)] px-3 py-2 text-[var(--jb-muted)] hover:text-[var(--jb-navy)]"
                              data-testid={`reject-${r.id}`}
                            >
                              <X className="w-3.5 h-3.5" /> Reject
                            </button>
                          )}
                          <button
                            onClick={() => deleteReview(r.id)}
                            className="mono-cap inline-flex items-center gap-1.5 border border-[var(--jb-line)] px-3 py-2 text-[var(--jb-muted)] hover:text-red-600"
                            data-testid={`delete-${r.id}`}
                            aria-label="Delete review"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="serif italic text-[17px] leading-[1.55] text-[var(--jb-navy)]">
                        "{r.body}"
                      </p>
                    </motion.article>
                  ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-white border-[var(--jb-line)]" data-testid="detail-modal">
          {selected?.kind === "quote" && (
            <>
              <DialogHeader>
                <DialogTitle className="serif text-3xl text-[var(--jb-navy)]">
                  {selected.name}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-6">
                <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                  <div className="flex items-start gap-3"><Phone className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> <a href={`tel:${selected.phone}`}>{selected.phone}</a></div>
                  <div className="flex items-start gap-3"><Mail className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> <a href={`mailto:${selected.email}`} className="break-all">{selected.email}</a></div>
                  <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> {selected.from_postcode} → {selected.to_postcode}</div>
                  <div className="flex items-start gap-3"><CalIcon className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> {selected.move_date}</div>
                </div>
                <div>
                  <div className="mono-cap text-[var(--jb-muted)] mb-2">Service</div>
                  <div className="serif text-xl">{selected.service}</div>
                </div>
                {selected.notes && (
                  <div>
                    <div className="mono-cap text-[var(--jb-muted)] mb-2">Notes</div>
                    <p className="text-[15px] leading-[1.6]">{selected.notes}</p>
                  </div>
                )}
                <div className="border-t border-[var(--jb-line)] pt-6">
                  <div className="mono-cap text-[var(--jb-muted)] mb-3">Update status</div>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className="w-full border border-[var(--jb-line)] rounded-none px-4 py-3" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-[var(--jb-line)]">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {selected?.kind === "estimate" && (
            <>
              <DialogHeader>
                <DialogTitle className="serif text-3xl text-[var(--jb-navy)]">
                  <span className="italic text-[var(--jb-rust)]">£{selected.price}</span> · {selected.email}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-6">
                <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                  <div className="flex items-start gap-3"><Mail className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> <a href={`mailto:${selected.email}`} className="break-all">{selected.email}</a></div>
                  <div className="flex items-start gap-3"><CalIcon className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> {selected.move_date || "—"}</div>
                  <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> {selected.distance_miles} miles</div>
                  <div className="flex items-start gap-3"><PoundSterling className="w-4 h-4 text-[var(--jb-rust)] mt-0.5" /> £{selected.price} indicative</div>
                </div>
                <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                  <div>
                    <div className="mono-cap text-[var(--jb-muted)] mb-2">Crew</div>
                    <div className="serif text-xl">
                      {selected.crew === "2men" ? "2 Men + Van" : selected.crew === "1man" ? "1 Man + Van" : (selected.size || "—")}
                    </div>
                  </div>
                  <div>
                    <div className="mono-cap text-[var(--jb-muted)] mb-2">Hours booked</div>
                    <div className="serif text-xl">{selected.hours ? `${selected.hours} hours` : "—"}</div>
                  </div>
                  <div>
                    <div className="mono-cap text-[var(--jb-muted)] mb-2">Additional helpers</div>
                    <div className="serif text-xl">{selected.helpers ?? 0}</div>
                  </div>
                </div>
                <div className="border-t border-[var(--jb-line)] pt-6 flex gap-3">
                  <a
                    href={`mailto:${selected.email}?subject=Your%20Jaybliss%20Logistics%20Quote&body=Hi%2C%0A%0AThanks%20for%20using%20our%20estimator.%20Your%20indicative%20price%20was%20%C2%A3${selected.price}.%0A%0AWe%27d%20love%20to%20confirm%20the%20booking.%20Please%20reply%20with%20your%20phone%20number%20and%20we%27ll%20be%20in%20touch.%0A%0AJaybliss%20Logistics`}
                    className="cta-primary bg-[var(--jb-navy)] text-[var(--jb-bone)] mono-cap px-5 py-3"
                    data-testid="email-lead-btn"
                  >
                    Email this lead →
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
